import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, resolveImg } from "../lib/api";
import { X, Copy, Smartphone, Mail, Send as SendIcon, Loader2, Download } from "lucide-react";

/**
 * Minimal, reliable share sheet.
 * - Primary action: the device's native share with the actual card IMAGE attached
 *   (covers Messages, WhatsApp, Messenger, Instagram, Facebook, etc.).
 * - In-app email: we send the greeting (card image embedded) ourselves.
 * - Copy link / Save image as fallbacks.
 * The greeting is only marked "sent" when the user explicitly confirms (or a
 * successful in-app email), so nothing is falsely counted.
 */
export function ShareSheet({ open, url, text, greetingId, cardImageUrl, originUrl, onClose, onShared }) {
  const [emailMode, setEmailMode] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [confirmVia, setConfirmVia] = useState(null);

  useEffect(() => {
    if (open) { setConfirmVia(null); setEmailMode(false); setEmailValue(""); }
  }, [open]);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied! Paste it anywhere."); }
    catch { toast("Copy this link: " + url); }
  };

  const saveImage = async () => {
    if (!cardImageUrl) return;
    try {
      const resp = await fetch(resolveImg(cardImageUrl));
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "greeting.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("Card image saved!");
    } catch { toast.error("Couldn't save the image. Try again."); }
  };

  // Shares a CLICKABLE link (with the card as its rich preview image) so the
  // recipient can tap through to the greeting and discover SendOccasions.
  const shareToApps = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "SendOccasions", text, url });
        setConfirmVia("your apps");
      } catch (e) { /* user cancelled — do nothing */ }
    } else {
      await copyLink();
      toast.message("Your browser can't open the share menu — link copied instead.");
      setConfirmVia("a copied link");
    }
  };

  const submitEmail = async () => {
    if (!emailValue) { toast("Enter a recipient email."); return; }
    setEmailing(true);
    try {
      await api.post(`/greetings/${greetingId}/email`, { recipient_email: emailValue, origin_url: originUrl });
      toast.success(`Greeting emailed to ${emailValue}! 🎉`);
      setEmailMode(false);
      setEmailValue("");
      onShared && onShared();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail || err.message));
    } finally {
      setEmailing(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="share-sheet" onClick={onClose}>
      <div
        className="w-full sm:max-w-md bg-[#FDFBF7] border-2 border-[#0A0A0A] rounded-t-2xl sm:rounded-2xl shadow-[0_-8px_0px_#0A0A0A] sm:shadow-[8px_8px_0px_#0A0A0A] max-h-[92vh] flex flex-col animate-pop-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b-2 border-[#0A0A0A]">
          <h2 className="text-xl font-display font-bold">Send your greeting</h2>
          <button onClick={onClose} data-testid="share-sheet-close" className="w-9 h-9 grid place-items-center rounded-xl border-2 border-[#0A0A0A] bg-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {/* Confirmation banner — greeting is only marked "sent" when the user taps "Yes" */}
          {confirmVia && (
            <div className="nb-card p-4 bg-[#E8F8F0]" data-testid="share-confirm">
              <div className="font-display font-bold mb-1">Did you send it? 🎉</div>
              <p className="text-sm text-[#4A4A4A] mb-3">
                Tap below once you've actually sent your greeting via {confirmVia}. We'll mark it as sent then.
              </p>
              <div className="flex gap-2">
                <button onClick={() => { setConfirmVia(null); onShared && onShared(); }} data-testid="share-confirm-yes" className="nb-btn bg-[#A8E6CF] flex-1 py-2.5">
                  Yes, I sent it
                </button>
                <button onClick={() => setConfirmVia(null)} data-testid="share-confirm-no" className="nb-btn bg-white px-4 py-2.5">
                  Not yet
                </button>
              </div>
            </div>
          )}

          {/* Primary: native share with the image */}
          <button onClick={shareToApps} data-testid="share-native-btn" className="nb-btn bg-[#A8E6CF] w-full py-4 text-lg justify-start gap-3">
            <span className="w-10 h-10 grid place-items-center rounded-xl border-2 border-[#0A0A0A] bg-white shrink-0"><Smartphone className="w-5 h-5" /></span>
            <span className="text-left leading-tight">
              Share to any app
              <span className="block text-xs font-semibold text-[#0A0A0A]/70">Sends a tap-to-open link — recipients see the card & can make their own</span>
            </span>
          </button>

          {/* In-app email */}
          {emailMode ? (
            <div className="nb-card p-4 bg-[#FFF7E6]" data-testid="sheet-email-form">
              <div className="flex items-center gap-2 font-display font-bold mb-2"><Mail className="w-5 h-5" /> Email this greeting</div>
              <div className="flex gap-2">
                <input type="email" value={emailValue} onChange={(e) => setEmailValue(e.target.value)} data-testid="sheet-email-input"
                  placeholder="recipient@email.com" autoFocus
                  className="flex-1 min-w-0 nb-border rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#A8E6CF]" />
                <button onClick={submitEmail} disabled={emailing} data-testid="sheet-email-send" className="nb-btn bg-[#F9F871] px-4 py-2.5 shrink-0">
                  {emailing ? <Loader2 className="w-5 h-5 animate-spin" /> : <SendIcon className="w-5 h-5" />}
                </button>
                <button onClick={() => setEmailMode(false)} className="w-10 h-10 grid place-items-center rounded-xl border-2 border-[#0A0A0A] bg-white shrink-0"><X className="w-4 h-4" /></button>
              </div>
            </div>
          ) : (
            <button onClick={() => setEmailMode(true)} data-testid="share-app-emailapp" className="nb-btn bg-[#FFB5A7] w-full py-4 text-lg justify-start gap-3">
              <span className="w-10 h-10 grid place-items-center rounded-xl border-2 border-[#0A0A0A] bg-white shrink-0"><Mail className="w-5 h-5" /></span>
              <span className="text-left leading-tight">
                Email it for me
                <span className="block text-xs font-semibold text-[#0A0A0A]/70">We'll deliver it straight to their inbox</span>
              </span>
            </button>
          )}

          {/* Copy link */}
          <button onClick={() => { copyLink(); setConfirmVia("a copied link"); }} data-testid="share-app-copy" className="nb-btn bg-white w-full py-4 text-lg justify-start gap-3">
            <span className="w-10 h-10 grid place-items-center rounded-xl border-2 border-[#0A0A0A] bg-[#F9F871] shrink-0"><Copy className="w-5 h-5" /></span>
            <span className="text-left">Copy link</span>
          </button>

          {/* Save image */}
          {cardImageUrl && (
            <button onClick={saveImage} data-testid="download-card-btn" className="nb-btn bg-white w-full py-4 text-lg justify-start gap-3">
              <span className="w-10 h-10 grid place-items-center rounded-xl border-2 border-[#0A0A0A] bg-[#C8B6FF] shrink-0"><Download className="w-5 h-5" /></span>
              <span className="text-left">Save card as image</span>
            </button>
          )}
        </div>

        {/* Sticky Cancel */}
        <div className="p-4 border-t-2 border-[#0A0A0A] bg-[#FDFBF7]">
          <button onClick={onClose} data-testid="share-sheet-cancel" className="nb-btn bg-white w-full py-3.5 text-lg">Cancel</button>
        </div>
      </div>
    </div>
  );
}
